import { useState, useRef, useEffect } from 'react'
import { useStore } from '../store/useStore'
import { Send, Sparkles, Copy, Check, Settings, StopCircle, Book, Brain, ThumbsUp, ThumbsDown, RefreshCw, Clock, Undo2, Zap } from 'lucide-react'
import { callAI, stopAI } from '../services/ai'
import { showToast } from './ui/Toast'
import AiSettings from './AiSettings'
import LibraryReference from './LibraryReference'
import AiHistory from './AiHistory'
import { smartMergeCode, validateJS } from '../utils/codeMerge'
import { logIteration, updateIteration, captureSketchState } from '../utils/researchLogger'
import { getDeviceContextForAI } from '../utils/deviceCapabilities'
import { trackPromptSubmit } from '../utils/analytics'

// Turbo Mode: Errors to ignore (not actual sketch bugs)
const IGNORED_ERRORS = [
  'Tracking Prevention blocked access to storage',
  'allow-scripts and allow-same-origin for its sandbox',
  'Failed to execute \'postMessage\'',
  'ResizeObserver loop',
  'Blocked autofocusing',
  'Script error.',
  'Load failed',
  'NetworkError',
  'Failed to fetch',
  'The operation was aborted',
  'cancelled',
  'isTrusted',  // Event objects stringified from resource load failures (e.g., image 404)
]

// Helper: Check if an error should be ignored by Turbo mode
function isIgnorableError(errorMessage) {
  if (!errorMessage) return true
  const msg = String(errorMessage).toLowerCase()
  return IGNORED_ERRORS.some(pattern => msg.includes(pattern.toLowerCase()))
}

// Helper: Extract the full error message from a console message object
function extractErrorMessage(msg) {
  // Handle different message formats from Preview.jsx console interception
  let errorText = ''
  
  // Primary: the message field
  if (msg.message) {
    errorText = msg.message
  }
  
  // Also check error object (from serialized Error)
  if (msg.error && typeof msg.error === 'object') {
    if (msg.error.message) {
      errorText = msg.error.message
    }
    if (msg.error.name) {
      errorText = `${msg.error.name}: ${errorText}`
    }
  }
  
  // Include stack trace for context
  const stack = msg.stack || msg.error?.stack || ''
  
  return { message: errorText, stack }
}

// Helper: Filter console messages to get only real sketch errors
function getRealSketchErrors(consoleMessages) {
  if (!consoleMessages || !Array.isArray(consoleMessages)) {
    console.log('[Turbo] No console messages to check')
    return []
  }
  
  const errors = consoleMessages.filter(msg => {
    if (msg.level !== 'error') return false
    
    const { message } = extractErrorMessage(msg)
    
    // Debug: log what we're checking
    console.log('[Turbo] Checking error:', message)
    
    if (isIgnorableError(message)) {
      console.log('[Turbo] Ignored:', message.substring(0, 50))
      return false
    }
    
    return true
  })
  
  console.log(`[Turbo] Found ${errors.length} real errors out of ${consoleMessages.length} messages`)
  return errors
}

// Helper: Show browser notification (only if tab not focused)
function showBrowserNotification(title, body) {
  if (!('Notification' in window)) return
  if (Notification.permission !== 'granted') return
  if (document.hasFocus()) return // Don't notify if user is on the tab
  
  const notification = new Notification(title, {
    body: body.substring(0, 100) + (body.length > 100 ? '...' : ''),
    icon: '/favicon.ico',
    tag: 'ai-complete' // Prevents duplicate notifications
  })
  
  notification.onclick = () => {
    window.focus()
    notification.close()
  }
  
  // Auto-close after 5 seconds
  setTimeout(() => notification.close(), 5000)
}

// Helper: Check for active/completed job that needs recovery
async function checkForActiveJob() {
  try {
    const activeJobData = localStorage.getItem('p5ai_active_job')
    if (!activeJobData) return null
    
    const { jobId, deviceId, startedAt } = JSON.parse(activeJobData)
    if (!jobId || !deviceId) return null
    
    // Check job status on server
    const response = await fetch(`/api/ai-resume?job=${jobId}&device=${deviceId}`)
    if (!response.ok) {
      // Job not found - clear stale localStorage
      localStorage.removeItem('p5ai_active_job')
      return null
    }
    
    const job = await response.json()
    
    // If job is complete or has any content (response OR thinking), return it for recovery
    const hasContent = job.response || job.thinking
    if (job.status === 'complete' || job.status === 'error' || (job.status === 'streaming' && hasContent)) {
      return job
    }
    
    // Job is still streaming with no content yet - check if it's been too long
    const startTime = new Date(startedAt).getTime()
    const elapsed = Date.now() - startTime
    if (elapsed > 5 * 60 * 1000) { // 5 minutes timeout
      localStorage.removeItem('p5ai_active_job')
      return null
    }
    
    return job
  } catch (e) {
    console.warn('[AI Recovery] Check failed:', e)
    return null
  }
}

export default function AiAssistantSimple() {
  const [showSettings, setShowSettings] = useState(false)
  const [showLibraryRef, setShowLibraryRef] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [currentThinking, setCurrentThinking] = useState('') // Real-time thinking display
  const [streamingContent, setStreamingContent] = useState('') // Real-time content streaming
  
  const {
    aiMessages,
    isAiThinking,
    aiProvider,
    aiModel,
    files,
    consoleMessages,
    p5Version,
    participantId,
    sessionId,
    iterationIndex,
    currentIterationId,
    currentSketchBefore,
    recoveredJob,
    aiChangeHistory,
    turboMode,
    turboRetryCount,
    turboMaxRetries,
    turboOriginalPrompt,
    turboChangeHistory,
    lastCanvasImage,
    addAiMessage,
    updateAiMessage,
    setAiThinking,
    setCode,
    setAiModel,
    setAiProvider,
    setCurrentIteration,
    incrementIterationIndex,
    setRecoveredJob,
    clearRecoveredJob,
    undoLastAIChange,
    setTurboMode,
    startTurboSession,
    incrementTurboRetry,
    addTurboAttempt,
    resetTurboSession,
    setLastCanvasImage
  } = useStore()
  
  // State for recovery UI
  const [isCheckingRecovery, setIsCheckingRecovery] = useState(false)

  // OpenAI mode combines model + reasoning
  const [openAIMode, setOpenAIMode] = useState('high') // 'none', 'low', 'high', 'codex'
  
  // Xelsed model selection - initialize from store
  const [xelsedModel, setXelsedModel] = useState(aiModel || 'xelsed-alpha06-flash')
  
  const [input, setInput] = useState('')
  const [copiedIndex, setCopiedIndex] = useState(null)
  const [showApiKeyBanner, setShowApiKeyBanner] = useState(false)
  const messagesEndRef = useRef(null)
  const thinkingAccumulatorRef = useRef('') // Ref to accumulate thinking for research logging
  
  // Research tracking
  const [promptStartTime, setPromptStartTime] = useState(null)
  const [messageRatings, setMessageRatings] = useState({}) // { messageIndex: { rating: 'up'/'down', comment: '', showComment: false } }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(scrollToBottom, [aiMessages])

  // Check for recoverable jobs on mount
  useEffect(() => {
    const checkRecovery = async () => {
      setIsCheckingRecovery(true)
      try {
        const job = await checkForActiveJob()
        // Show recovery if job has any content (response OR thinking)
        const hasContent = job?.response || job?.thinking
        
        if (job && job.status === 'complete' && hasContent) {
          console.log('[AI Recovery] Found completed job:', job.id)
          setRecoveredJob(job)
          showToast('Previous AI response recovered!', 'success')
        } else if (job && job.status === 'streaming' && hasContent) {
          console.log('[AI Recovery] Found in-progress job with partial content:', job.id, { response: !!job.response, thinking: !!job.thinking })
          setRecoveredJob(job)
          showToast('Found in-progress AI response', 'info')
        } else if (job && job.status === 'error') {
          console.log('[AI Recovery] Found errored job:', job.id)
          // Optionally show errored jobs too
          if (hasContent) {
            setRecoveredJob(job)
            showToast('Previous AI attempt had an error but may have partial content', 'warning')
          }
        }
      } catch (e) {
        console.warn('[AI Recovery] Error:', e)
      } finally {
        setIsCheckingRecovery(false)
      }
    }
    
    checkRecovery()
  }, [setRecoveredJob])
  
  // Handle applying recovered job to chat
  const applyRecoveredJob = async () => {
    if (!recoveredJob) return
    
    // Add the original prompt as user message
    addAiMessage({
      role: 'user',
      content: recoveredJob.prompt,
      displayContent: recoveredJob.prompt
    })
    
    // Add the recovered response as assistant message
    // If no response but has thinking, show that the AI was still processing
    const responseContent = recoveredJob.response || 
      (recoveredJob.thinking ? '[AI was still thinking when interrupted - see reasoning below]' : '[No response recovered]')
    
    addAiMessage({
      role: 'assistant',
      content: responseContent,
      reasoning: recoveredJob.thinking || '',
      recovered: true
    })
    
    // Log to research iterations with recovered flag
    if (participantId && sessionId) {
      await logIteration({
        participantId,
        sessionId,
        iterationIndex: iterationIndex,
        promptText: recoveredJob.prompt,
        responseText: responseContent,
        thinkingText: recoveredJob.thinking || '',
        sketchBefore: captureSketchState(),
        provider: recoveredJob.provider || 'unknown',
        model: recoveredJob.model || 'unknown',
        timeToPromptSec: null,
        recovered: true,
        aiJobId: recoveredJob.id
      })
      incrementIterationIndex()
    }
    
    // Clear recovery state
    clearRecoveredJob()
    try {
      localStorage.removeItem('p5ai_active_job')
    } catch (e) {}
    
    showToast('Recovered content added to chat', 'success')
  }
  
  // Dismiss recovered job
  const dismissRecoveredJob = () => {
    clearRecoveredJob()
    try {
      localStorage.removeItem('p5ai_active_job')
    } catch (e) {}
  }

  // Get the actual model and reasoning based on mode
  const getOpenAIConfig = (mode) => {
    switch(mode) {
      case 'none':
        return { model: 'gpt-5.1', reasoningMode: 'none' }
      case 'low':
        return { model: 'gpt-5.1', reasoningMode: 'low' }
      case 'medium':
        return { model: 'gpt-5.1', reasoningMode: 'medium' }
      case 'high':
        return { model: 'gpt-5.1', reasoningMode: 'high' }
      case 'codex':
        return { model: 'gpt-5.1-codex', reasoningMode: 'low' }
      default:
        return { model: 'gpt-5.1', reasoningMode: 'none' } // GPT-5.1 defaults to 'none'
    }
  }

  const handleSend = async () => {
    if (!input.trim() || isAiThinking) return
    
    // Track prompt submission for Google Ads conversion
    // Use defaults if localStorage is blocked by tracking prevention
    let surveyCompleted = false
    let isDyslexic = false
    try {
      const profile = localStorage.getItem('p5ai_research_profile_v1')
      const profileData = profile ? JSON.parse(profile) : null
      surveyCompleted = profileData?.answered === true
      isDyslexic = surveyCompleted && localStorage.getItem('p5ai_dyslexic_status') === 'yes'
    } catch (e) {
      // localStorage blocked by tracking prevention - use defaults
    }
    // Always fire tracking - Zaraz handles the rest server-side
    trackPromptSubmit(surveyCompleted, isDyslexic, aiProvider)
    
    // Mark that user has submitted their first prompt (triggers notification prompt)
    if (!useStore.getState().hasSubmittedFirstPrompt) {
      useStore.setState({ hasSubmittedFirstPrompt: true })
    }

    // On first message, include device capabilities context
    let messageContent = input
    if (aiMessages.length === 0) {
      try {
        const deviceContext = await getDeviceContextForAI()
        messageContent = `${deviceContext}\n\n${input}`
        console.log('[AI] First message - injected device context:', deviceContext)
      } catch (e) {
        console.warn('[AI] Could not detect device capabilities:', e)
      }
    }

    const userMessage = {
      role: 'user',
      content: messageContent,
      displayContent: input // Store original for display
    }
    
    // Research: Calculate time to prompt
    const timeToPromptSec = promptStartTime ? Math.floor((Date.now() - promptStartTime) / 1000) : null
    
    // Research: Get FRESH values from store (avoid stale closure after survey skip)
    let freshState = useStore.getState()
    let currentParticipantId = freshState.participantId
    let currentSessionId = freshState.sessionId
    let currentIterationIdx = freshState.iterationIndex
    
    // Auto-setup tracking: create participant if needed, then set up session
    if (!currentParticipantId || !currentSessionId) {
      try {
        let profile = localStorage.getItem('p5ai_research_profile_v1')
        let data = profile ? JSON.parse(profile) : null
        
        // If no participant exists, create one now (before AI call)
        if (!data || !data.participantId) {
          const newParticipantId = crypto.randomUUID()
          data = {
            version: 1,
            participantId: newParticipantId,
            answered: false,
            timestamp: new Date().toISOString()
          }
          localStorage.setItem('p5ai_research_profile_v1', JSON.stringify(data))
          console.log('[Research] Created pending participant:', newParticipantId)
        }
        
        // Set up session tracking
        currentParticipantId = data.participantId
        currentSessionId = crypto.randomUUID()
        useStore.getState().setResearchParticipant(currentParticipantId)
        useStore.getState().setResearchSession(currentSessionId)
        currentIterationIdx = 0
        console.log('[Research] Session tracking initialized:', { currentParticipantId, currentSessionId })
      } catch (e) {
        console.warn('[Research] Cannot setup tracking:', e.message)
      }
    }
    
    // Debug: Log research tracking status
    if (!currentParticipantId || !currentSessionId) {
      console.warn('[Research] Tracking not active:', { currentParticipantId, currentSessionId })
    }
    
    // Research: Close out previous iteration's sketchAfter before starting new one
    // This ensures before/after line up even if user didn't click Apply
    const prevIterationId = freshState.currentIterationId
    const prevSketchBefore = freshState.currentSketchBefore
    if (prevIterationId && prevSketchBefore) {
      const currentSketchState = captureSketchState()
      updateIteration({
        iterationId: prevIterationId,
        sketchBefore: prevSketchBefore,
        sketchAfter: currentSketchState
      })
      console.log('[Research] Auto-captured sketchAfter for previous iteration:', prevIterationId)
    }
    
    // Research: Capture sketch before prompt (this becomes sketchBefore for the NEW iteration)
    const sketchBefore = currentParticipantId && currentSessionId ? captureSketchState() : null

    addAiMessage(userMessage)
    setInput('')
    setAiThinking(true)
    setCurrentThinking('') // Reset thinking display
    setStreamingContent('') // Reset streaming content
    thinkingAccumulatorRef.current = '' // Reset accumulator for research logging

    try {
      let model = 'gpt-5.1'
      let reasoningMode = 'low'
      let thinkingLevel = 'high' // For Gemini
      
      if (aiProvider === 'openai') {
        const config = getOpenAIConfig(openAIMode)
        model = config.model
        reasoningMode = config.reasoningMode
      } else if (aiProvider === 'anthropic') {
        model = 'claude-sonnet-4-20250514'
      } else if (aiProvider === 'kimi') {
        model = 'kimi-k2-turbo-preview'
      } else if (aiProvider === 'gemini') {
        model = 'gemini-3-pro-preview'
        thinkingLevel = 'high' // Gemini 3 Pro thinking level
      } else if (aiProvider === 'xelsed') {
        model = xelsedModel
      }

      // Set up thinking callback for real-time updates
      // Uses ref for research logging (survives async), state for UI display
      const onThinking = (thinkingDelta) => {
        thinkingAccumulatorRef.current += thinkingDelta // For research logging
        setCurrentThinking(prev => prev + thinkingDelta) // For UI display
      }

      // Set up delta callback for real-time content streaming
      const onDelta = (contentDelta) => {
        setStreamingContent(prev => prev + contentDelta)
      }

      const response = await callAI(
        [...aiMessages, userMessage], 
        files, 
        consoleMessages, 
        p5Version, 
        aiProvider, 
        model,
        reasoningMode,
        onThinking, // Pass callback for thinking updates
        thinkingLevel, // Pass thinking level for Gemini
        onDelta // Pass callback for content streaming
      )
      
      // Add assistant message and get its index for later iteration ID update
      const assistantMessageIndex = addAiMessage({
        role: 'assistant',
        content: response.content,
        reasoning: response.reasoning,
        code: response.code,
        fileName: response.fileName
      })
      
      // Research: Log iteration (use fresh values captured at start of handleSend)
      if (currentParticipantId && currentSessionId && sketchBefore) {
        const iterationId = await logIteration({
          participantId: currentParticipantId,
          sessionId: currentSessionId,
          iterationIndex: currentIterationIdx,
          promptText: input,
          responseText: response.content,
          thinkingText: thinkingAccumulatorRef.current, // Use ref value (not stale state)
          sketchBefore,
          provider: aiProvider,
          model,
          timeToPromptSec
        })
        
        if (iterationId) {
          // Update the assistant message with its iterationId for accurate rating attribution
          updateAiMessage(assistantMessageIndex, { iterationId })
          setCurrentIteration(iterationId, sketchBefore)
          incrementIterationIndex()
        }
      }

      // If AI provided code, optionally apply it
      if (response.code && response.shouldApply) {
        setCode(response.code)
        showToast(`AI code applied to editor${response.fileName ? ` (${response.fileName})` : ''}`, 'success')
      }
      
      // Show browser notification (only if tab not focused)
      showBrowserNotification('AI Response Ready', response.content || 'Your AI response is ready!')
      
      // TURBO MODE: Auto-apply, run, and monitor for errors
      if (turboMode && response.content) {
        await handleTurboFlow(response.content, input)
      }
      
      // Trigger survey after first AI response (if not already completed)
      try {
        const hasUsedBefore = localStorage.getItem('p5ai_has_used_app') === 'true'
        if (!hasUsedBefore) {
          localStorage.setItem('p5ai_has_used_app', 'true')
          // Small delay to let user see the AI response first
          setTimeout(() => {
            useStore.getState().triggerSurvey()
          }, 2000)
        }
      } catch (e) {}
    } catch (error) {
      handleError(error)
    } finally {
      setAiThinking(false)
      setCurrentThinking('') // Clear thinking when done
      setStreamingContent('') // Clear streaming content when done
    }
  }

  const handleError = (error) => {
    if (error.name === 'AbortError' || error.message === 'Request cancelled') {
      addAiMessage({
        role: 'assistant',
        content: '⏹️ Request cancelled by user',
        isError: false
      })
      showToast('AI request cancelled', 'info')
    } else {
      let errorMsg = error.message
      const needsApiKey = errorMsg.includes('API key') || errorMsg.includes('504') || errorMsg.includes('Gateway Timeout') || errorMsg.includes('quota')
      
      if (needsApiKey) {
        setShowApiKeyBanner(true)
        errorMsg = '🔑 The free API quota has been exceeded. Please add your own API key to continue using the AI assistant.'
      }
      
      addAiMessage({
        role: 'assistant',
        content: `Sorry, I encountered an error:\n${errorMsg}`,
        isError: true
      })
      showToast('AI request failed', 'error')
    }
  }

  const handleStop = () => {
    stopAI()
    setAiThinking(false)
    setCurrentThinking('')
    setStreamingContent('')
    resetTurboSession()
    showToast('Stopping AI request...', 'info')
  }

  // TURBO MODE: Auto-apply code, run sketch, monitor for errors, auto-fix
  const handleTurboFlow = async (responseContent, originalPrompt) => {
    // Debug: Log what we received
    console.log('[Turbo] Response content length:', responseContent?.length || 0)
    console.log('[Turbo] Response preview:', responseContent?.substring(0, 200))
    
    // Initialize turbo session on first call
    if (turboRetryCount === 0) {
      startTurboSession(originalPrompt)
    }
    
    // Extract code blocks from response
    const codeBlocks = []
    const codeBlockRegex = /```(?:(\w+))?(?:\s+([\w.-]+\.[\w]+))?[\r\n]*([\s\S]*?)```/g
    let match
    
    while ((match = codeBlockRegex.exec(responseContent)) !== null) {
      const language = match[1]
      const fileNameOnOpenLine = match[2]
      let code = (match[3] || '').trim()
      
      // Debug: Log each match
      console.log('[Turbo] Found code block:', { language, fileNameOnOpenLine, codeLength: code.length })
      
      let fileName = fileNameOnOpenLine
      const firstLineMatch = code.match(/^(?:\/\/\s*|\/\*\s*|<!--\s*)([\w.-]+\.[\w]+)/)
      if (!fileName && firstLineMatch) {
        fileName = firstLineMatch[1]
        code = code.replace(/^(?:\/\/\s*|\/\*\s*|<!--\s*)[\w.-]+\.[\w]+(?:\s*\*\/|\s*-->)?\n?/, '').trim()
      }
      
      if (!fileName && language) {
        if (language === 'css') fileName = 'style.css'
        else if (language === 'html') fileName = 'index.html'
        else if (language === 'javascript' || language === 'js') {
          if (/\b(setup|draw|preload|mousePressed|keyPressed)\s*\(/.test(code)) {
            fileName = 'sketch.js'
          }
        }
      }
      
      // Validate: code must be non-empty and have meaningful content (>10 chars)
      if (code && code.length > 10) {
        codeBlocks.push({ fileName: fileName || 'sketch.js', code })
        console.log('[Turbo] Valid code block for:', fileName || 'sketch.js', '- length:', code.length)
      } else {
        console.log('[Turbo] Skipping empty/tiny code block:', code?.substring(0, 50))
      }
    }
    
    if (codeBlocks.length === 0) {
      console.log('[Turbo] No valid code blocks found in response')
      showToast('⚡ Turbo: No code blocks found to apply', 'warning')
      resetTurboSession()
      return
    }
    
    // Check if any code blocks are actually different from existing files
    const currentFiles = useStore.getState().files
    let actualChanges = 0
    
    codeBlocks.forEach(block => {
      const targetFile = currentFiles.find(f => f.name === block.fileName)
      const existingContent = targetFile?.content || ''
      
      // Normalize for comparison (trim whitespace)
      const normalizedNew = block.code.trim()
      const normalizedExisting = existingContent.trim()
      
      if (normalizedNew !== normalizedExisting) {
        actualChanges++
        console.log('[Turbo] File will change:', block.fileName)
      } else {
        console.log('[Turbo] File IDENTICAL, skipping:', block.fileName)
      }
    })
    
    if (actualChanges === 0) {
      console.log('[Turbo] No actual changes to apply - all code identical to existing')
      showToast('⚡ Turbo: Code unchanged, waiting for AI to provide new code...', 'warning')
      resetTurboSession()
      return
    }
    
    // Clear console BEFORE applying - avoid race condition with useEffect
    useStore.getState().clearConsole()
    
    // Auto-apply all code blocks that have changes
    showToast(`⚡ Turbo: Applying ${actualChanges} change(s)...`, 'info')
    
    codeBlocks.forEach(block => {
      const targetFile = currentFiles.find(f => f.name === block.fileName)
      if (targetFile) {
        const normalizedNew = block.code.trim()
        const normalizedExisting = targetFile.content.trim()
        
        // Only apply if actually different
        if (normalizedNew !== normalizedExisting) {
          const mergedCode = smartMergeCode(targetFile.content, block.code, targetFile.type)
          useStore.getState().saveAIChange(block.fileName, targetFile.content, mergedCode)
          useStore.getState().updateFileContent(block.fileName, mergedCode, block.fileName === 'sketch.js')
          console.log('[Turbo] Applied changes to:', block.fileName)
        }
      } else {
        useStore.getState().addFile(block.fileName, block.code)
        useStore.getState().saveAIChange(block.fileName, '', block.code)
        console.log('[Turbo] Created new file:', block.fileName)
      }
    })
    
    // Let React re-render with new files - the useEffect in Preview.jsx will auto-run
    // Do NOT dispatch runSketch event here (causes race condition with stale closure)
    
    // ========== TWO-PHASE ERROR DETECTION ==========
    // PHASE 1: Quick check for immediate JS errors (500ms)
    await new Promise(resolve => setTimeout(resolve, 500))
    
    let currentConsole = useStore.getState().consoleMessages
    let realErrors = getRealSketchErrors(currentConsole)
    
    if (realErrors.length > 0) {
      // Found immediate errors - proceed to auto-fix
      console.log('[Turbo] Phase 1: Found immediate errors:', realErrors.length)
    } else {
      // PHASE 2: Wait longer for blank canvas / long task detection
      // 3500ms accounts for: ~1s iframe load + 2.5s blank canvas check
      console.log('[Turbo] Phase 1: No immediate errors, waiting for canvas check...')
      
      await new Promise(resolve => setTimeout(resolve, 3500))
      
      currentConsole = useStore.getState().consoleMessages
      realErrors = getRealSketchErrors(currentConsole)
      
      // Only retry for real errors, not performance warnings
      // Performance warnings (Violations, Long task) are expected for heavy sketches like particle systems
      if (realErrors.length === 0) {
        showToast('⚡ Turbo: Sketch running successfully!', 'success')
        resetTurboSession()
        return
      }
    }
    // ========== END TWO-PHASE DETECTION ==========
    
    // We have errors - check if we can retry
    const currentRetry = useStore.getState().turboRetryCount
    if (currentRetry >= turboMaxRetries) {
      showToast(`⚡ Turbo: Max retries (${turboMaxRetries}) reached. Manual fix needed.`, 'error')
      resetTurboSession()
      return
    }
    
    // Increment retry and attempt auto-fix
    incrementTurboRetry()
    const newRetryCount = currentRetry + 1
    
    // Build the auto-fix prompt with full context (use extractErrorMessage for proper format)
    const errorMessages = realErrors.map(e => {
      const { message, stack } = extractErrorMessage(e)
      // Include stack trace snippet for better context
      const stackPreview = stack ? `\nStack: ${stack.split('\n').slice(0, 3).join('\n')}` : ''
      return message + stackPreview
    }).join('\n\n')
    const turboOrigPrompt = useStore.getState().turboOriginalPrompt
    const changeHistory = useStore.getState().turboChangeHistory
    const canvasImage = useStore.getState().lastCanvasImage
    
    // Record this attempt with the actual code that was tried
    const codeChangeSummary = codeBlocks.map(b => ({
      fileName: b.fileName,
      codeSnippet: b.code.substring(0, 500) // First 500 chars as summary
    }))
    addTurboAttempt(errorMessages, codeBlocks.map(b => b.fileName).join(', '), codeChangeSummary)
    
    // Build comprehensive fix prompt
    let fixPrompt = `🔧 **TURBO AUTO-FIX (Attempt ${newRetryCount}/${turboMaxRetries})**\n\n`
    fixPrompt += `## Original Request\n"${turboOrigPrompt}"\n\n`
    fixPrompt += `## Current Error(s)\n\`\`\`\n${errorMessages}\n\`\`\`\n\n`
    
    // Include all project files
    fixPrompt += `## All Project Files (Current State)\n`
    const allFiles = useStore.getState().files
    allFiles.forEach(f => {
      fixPrompt += `\n### ${f.name}\n\`\`\`${f.type}\n${f.content}\n\`\`\`\n`
    })
    
    // Include previous attempts with actual code tried - CRITICAL for avoiding loops
    if (changeHistory.length > 0) {
      fixPrompt += `\n## Previous Fix Attempts (DO NOT REPEAT THESE - THEY FAILED)\n`
      changeHistory.forEach(attempt => {
        fixPrompt += `\n### Attempt ${attempt.attempt}: Applied to ${attempt.fixApplied}\n`
        fixPrompt += `**Error received:** ${attempt.error.substring(0, 150)}\n`
        if (attempt.codeChanges && attempt.codeChanges.length > 0) {
          fixPrompt += `**Code that was tried:**\n`
          attempt.codeChanges.forEach(change => {
            // Show relevant snippet of what was tried
            const snippet = change.codeSnippet.substring(0, 300)
            fixPrompt += `\`\`\`js\n// ${change.fileName}\n${snippet}...\n\`\`\`\n`
          })
        }
        fixPrompt += `**This approach FAILED. Try something different.**\n`
      })
      fixPrompt += `\n⚠️ **IMPORTANT:** The above approaches have already been tried and failed. You MUST try a genuinely different solution.\n`
    }
    
    fixPrompt += `\n## Instructions\nPlease analyze all the code and fix the error(s). Provide complete corrected files.`
    
    // Add canvas image context if available
    if (canvasImage) {
      fixPrompt += `\n\n[Canvas image attached showing current output]`
    }
    
    showToast(`⚡ Turbo: Requesting fix (${newRetryCount}/${turboMaxRetries})...`, 'info')
    
    // Add as user message (with condensed display)
    addAiMessage({
      role: 'user',
      content: fixPrompt,
      displayContent: `🔧 Turbo Auto-Fix (${newRetryCount}/${turboMaxRetries}): ${errorMessages.substring(0, 50)}...`,
      isAutoFix: true,
      canvasImage: canvasImage
    })
    
    setAiThinking(true)
    setCurrentThinking('')
    setStreamingContent('')
    
    try {
      let model = aiModel
      let reasoningMode = 'low'
      
      if (aiProvider === 'openai') {
        model = 'gpt-5.1'
        reasoningMode = 'low'
      } else if (aiProvider === 'anthropic') {
        model = 'claude-sonnet-4-20250514'
      } else if (aiProvider === 'gemini') {
        model = 'gemini-3-pro-preview'
      }
      
      const response = await callAI(
        [...aiMessages, { role: 'user', content: fixPrompt }],
        currentFiles,
        currentConsole,
        p5Version,
        aiProvider,
        model,
        reasoningMode,
        (delta) => setCurrentThinking(prev => prev + delta),
        'high',
        (delta) => setStreamingContent(prev => prev + delta)
      )
      
      addAiMessage({
        role: 'assistant',
        content: response.content,
        reasoning: response.reasoning,
        isAutoFix: true
      })
      
      // Recursively handle the turbo flow with the new response
      await handleTurboFlow(response.content, turboOrigPrompt)
      
    } catch (err) {
      addAiMessage({
        role: 'assistant',
        content: `⚡ Turbo auto-fix failed: ${err.message}`,
        isError: true
      })
      resetTurboSession()
    } finally {
      setAiThinking(false)
      setCurrentThinking('')
      setStreamingContent('')
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const copyCode = (code, index) => {
    navigator.clipboard.writeText(code)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
    showToast('Code copied to clipboard', 'success')
  }

  // Auto-fix: Send syntax errors back to AI for correction
  const requestAutoFix = async (code, error, fileName) => {
    if (isAiThinking) return // Don't stack requests
    
    // Add line numbers to help AI locate the error
    const codeWithLineNumbers = code.split('\n').map((line, i) => {
      const lineNum = i + 1
      const marker = lineNum === error.line ? '>>> ' : '    '
      return `${marker}${lineNum.toString().padStart(3)}: ${line}`
    }).join('\n')
    
    // Extract the problematic line for clarity
    const lines = code.split('\n')
    const errorLine = lines[error.line - 1] || ''
    const contextStart = Math.max(0, error.line - 3)
    const contextEnd = Math.min(lines.length, error.line + 2)
    const contextLines = lines.slice(contextStart, contextEnd).map((line, i) => {
      const lineNum = contextStart + i + 1
      const marker = lineNum === error.line ? '>>>' : '   '
      return `${marker} ${lineNum}: ${line}`
    }).join('\n')
    
    const fixPrompt = `🔧 **SYNTAX ERROR - Please fix the code you just provided**

**File:** \`${fileName || 'sketch.js'}\`
**Error Location:** Line ${error.line}, Column ${error.column}
**Error Message:** ${error.message}

**The problematic line:**
\`\`\`
${errorLine}
\`\`\`

**Context around the error:**
\`\`\`
${contextLines}
\`\`\`

**Full code with error marked (>>> indicates error line):**
\`\`\`javascript
${codeWithLineNumbers}
\`\`\`

Please provide the **complete corrected code** for \`${fileName || 'sketch.js'}\`. Fix the syntax error on line ${error.line} while keeping the rest of the code working.`

    // Add as user message
    addAiMessage({
      role: 'user',
      content: fixPrompt,
      displayContent: `🔧 Auto-fix: "${error.message}" on line ${error.line}`,
      isAutoFix: true
    })
    
    setAiThinking(true)
    setCurrentThinking('')
    setStreamingContent('')
    
    try {
      let model = 'gpt-5.1'
      let reasoningMode = 'low'
      
      if (aiProvider === 'openai') {
        const config = getOpenAIConfig(openAIMode)
        model = config.model
        reasoningMode = config.reasoningMode
      } else if (aiProvider === 'anthropic') {
        model = 'claude-sonnet-4-20250514'
      } else if (aiProvider === 'gemini') {
        model = 'gemini-3-pro-preview'
      } else if (aiProvider === 'xelsed') {
        model = xelsedModel
      }
      
      const response = await callAI(
        [...aiMessages, { role: 'user', content: fixPrompt }],
        files,
        consoleMessages,
        p5Version,
        aiProvider,
        model,
        reasoningMode,
        (delta) => setCurrentThinking(prev => prev + delta),
        'high',
        (delta) => setStreamingContent(prev => prev + delta)
      )
      
      addAiMessage({
        role: 'assistant',
        content: response.content,
        reasoning: response.reasoning,
        isAutoFix: true
      })
      
      showToast('🔧 AI provided a fix - click Apply to use it', 'success')
    } catch (err) {
      addAiMessage({
        role: 'assistant',
        content: `Could not auto-fix: ${err.message}`,
        isError: true
      })
    } finally {
      setAiThinking(false)
      setCurrentThinking('')
      setStreamingContent('')
    }
  }

  const applyCode = (code, fileName) => {
    // Validate JS code before applying
    const isJsFile = !fileName || fileName.endsWith('.js')
    if (isJsFile) {
      const validation = validateJS(code)
      if (!validation.valid) {
        showToast(`⚠️ Syntax error on line ${validation.error.line} - requesting AI fix...`, 'warning')
        // Auto-request fix from AI
        requestAutoFix(code, validation.error, fileName)
        return // Don't apply broken code
      }
    }
    
    if (fileName) {
      const targetFile = files.find(f => f.name === fileName)
      
      if (!targetFile) {
        const fileType = fileName.endsWith('.css') ? 'css' : fileName.endsWith('.html') ? 'html' : 'js'
        useStore.getState().addFile(fileName, code, fileType)
        // Save for undo (new file, beforeContent is empty)
        useStore.getState().saveAIChange(fileName, '', code)
        showToast(`✅ Created ${fileName} with AI code`, 'success')
      } else {
        const fileType = targetFile.type
        const mergedCode = smartMergeCode(targetFile.content, code, fileType)
        // Save for undo before applying
        useStore.getState().saveAIChange(fileName, targetFile.content, mergedCode)
        useStore.getState().updateFileContent(fileName, mergedCode, true)
        showToast(`✅ Applied code to ${fileName} (smart merged)`, 'success')
      }
    } else {
      const currentFileName = useStore.getState().currentFile
      const currentFile = files.find(f => f.name === currentFileName)
      
      if (currentFile) {
        const mergedCode = smartMergeCode(currentFile.content, code, currentFile.type)
        // Save for undo before applying
        useStore.getState().saveAIChange(currentFileName, currentFile.content, mergedCode)
        setCode(mergedCode)
        showToast('✅ Applied to current file (smart merged)', 'success')
      } else {
        setCode(code)
        showToast('✅ Code applied', 'success')
      }
    }
    
    // Research: Update iteration with sketch after code applied
    if (currentIterationId && currentSketchBefore) {
      setTimeout(() => {
        const sketchAfter = captureSketchState()
        updateIteration({
          iterationId: currentIterationId,
          sketchBefore: currentSketchBefore,
          sketchAfter,
          appliedSuggestion: true
        })
      }, 100) // Small delay to ensure state updates
    }
  }

  const applyAllCode = (codeBlocks) => {
    if (!codeBlocks || codeBlocks.length === 0) return
    
    // Validate all JS blocks first and collect errors
    const jsErrors = []
    codeBlocks.forEach((block, idx) => {
      const isJsFile = !block.fileName || block.fileName.endsWith('.js')
      if (isJsFile) {
        const validation = validateJS(block.code)
        if (!validation.valid) {
          jsErrors.push(`${block.fileName || 'code block ' + (idx + 1)}: line ${validation.error.line} - ${validation.error.message}`)
        }
      }
    })
    
    // Show warning if any JS blocks have syntax errors
    if (jsErrors.length > 0) {
      showToast(`⚠️ ${jsErrors.length} code block(s) have syntax errors`, 'warning')
      console.warn('[AI Code Validation] Syntax errors found:', jsErrors)
    }
    
    let appliedCount = 0
    const appliedFiles = []
    const activeFile = useStore.getState().currentFile
    let activeFileUpdated = false
    let activeFileNewContent = null
    
    codeBlocks.forEach(block => {
      const fileName = block.fileName
      
      if (fileName) {
        // Get fresh file state for each iteration
        const currentFiles = useStore.getState().files
        const targetFile = currentFiles.find(f => f.name === fileName)
        
        if (!targetFile) {
          // Create new file
          const fileType = fileName.endsWith('.css') ? 'css' : fileName.endsWith('.html') ? 'html' : 'js'
          useStore.getState().addFile(fileName, block.code, fileType)
          appliedFiles.push(`+ ${fileName}`)
        } else {
          // Merge with existing file
          const mergedCode = smartMergeCode(targetFile.content, block.code, targetFile.type)
          useStore.getState().updateFileContent(fileName, mergedCode, false)
          appliedFiles.push(`✓ ${fileName}`)
          
          // Track if we updated the active file
          if (fileName === activeFile) {
            activeFileUpdated = true
            activeFileNewContent = mergedCode
          }
        }
        appliedCount++
      } else {
        // Apply to current file
        const currentFiles = useStore.getState().files
        const currentFileName = useStore.getState().currentFile
        const currentFile = currentFiles.find(f => f.name === currentFileName)
        
        if (currentFile) {
          const mergedCode = smartMergeCode(currentFile.content, block.code, currentFile.type)
          useStore.getState().updateFileContent(currentFileName, mergedCode, false)
          appliedFiles.push(`✓ ${currentFileName}`)
          appliedCount++
          activeFileUpdated = true
          activeFileNewContent = mergedCode
        }
      }
    })
    
    // Update the editor if the active file was modified
    if (activeFileUpdated && activeFileNewContent !== null) {
      setCode(activeFileNewContent)
    }
    
    // Show summary toast
    if (appliedCount > 0) {
      showToast(`✅ Applied ${appliedCount} code block${appliedCount > 1 ? 's' : ''}: ${appliedFiles.join(', ')}`, 'success')
    }
    
    // Research: Update iteration with sketch after all code applied
    if (currentIterationId && currentSketchBefore) {
      setTimeout(() => {
        const sketchAfter = captureSketchState()
        updateIteration({
          iterationId: currentIterationId,
          sketchBefore: currentSketchBefore,
          sketchAfter,
          appliedSuggestion: true
        })
      }, 100)
    }
  }
  
  const createFile = (fileName, code) => {
    const fileType = fileName.endsWith('.css') ? 'css' : fileName.endsWith('.html') ? 'html' : 'js'
    useStore.getState().addFile(fileName, code || '', fileType)
    showToast(`Created ${fileName}`, 'success')
  }

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user'
    
    // For user messages, use displayContent if available (hides device context)
    const messageText = isUser && message.displayContent ? message.displayContent : message.content
    
    // Extract code blocks with file names
    const codeBlocks = []
    // Flexible regex: handles optional language, optional filename, optional newlines
    const codeBlockRegex = /```(?:(\w+))?(?:\s+([\w.-]+\.[\w]+))?[\r\n]*([\s\S]*?)```/g
    let match
    
    while ((match = codeBlockRegex.exec(messageText)) !== null) {
      const language = match[1]
      const fileNameOnOpenLine = match[2]
      let code = (match[3] || '').trim()
      
      let fileName = fileNameOnOpenLine
      const firstLineMatch = code.match(/^(?:\/\/\s*|\/\*\s*|<!--\s*)([\w.-]+\.[\w]+)/)
      if (!fileName && firstLineMatch) {
        fileName = firstLineMatch[1]
        code = code.replace(/^(?:\/\/\s*|\/\*\s*|<!--\s*)[\w.-]+\.[\w]+(?:\s*\*\/|\s*-->)?\n?/, '').trim()
      }
      
      if (!fileName && language) {
        if (language === 'css') fileName = 'style.css'
        else if (language === 'html') fileName = 'index.html'
        else if (language === 'javascript' || language === 'js') {
          if (/\b(setup|draw|preload|mousePressed|keyPressed)\s*\(/.test(code)) {
            fileName = 'sketch.js'
          }
        }
      }
      
      codeBlocks.push({
        fileName: fileName || null,
        code: code
      })
    }
    
    // Remove code blocks from content to avoid duplication
    // Only show text explanation, not the code twice
    const contentWithoutCodeBlocks = messageText.replace(/```[\s\S]*?```/g, '').trim()
    
    // Detect file creation suggestions
    const fileCreationRegex = /create\s+(?:a\s+)?(?:new\s+)?(?:file\s+)?(?:called\s+)?[`'""]?(\w+\.(?:js|css|html))[`'""]?/gi
    const suggestedFiles = []
    let fileMatch
    while ((fileMatch = fileCreationRegex.exec(messageText)) !== null) {
      const fileName = fileMatch[1]
      if (!files.find(f => f.name === fileName)) {
        suggestedFiles.push(fileName)
      }
    }
    
    return (
      <div
        key={index}
        className={`flex gap-3 p-3 ${isUser ? 'bg-gray-800' : 'bg-gray-900'} rounded-lg`}
      >
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser ? 'bg-p5-pink' : 'bg-p5-blue'
        }`}>
          {isUser ? '👤' : '🤖'}
        </div>
        
        <div className="flex-1 space-y-2">
          {/* Recovered job indicator */}
          {message.recovered && (
            <div className="text-xs text-yellow-400 bg-yellow-900/30 px-2 py-1 rounded mb-2 flex items-center gap-1">
              <RefreshCw size={12} />
              Recovered from previous session
            </div>
          )}
          
          {/* Show reasoning if available - open by default for recovered messages */}
          {message.reasoning && (
            <details className="mb-2" open={message.recovered}>
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300 flex items-center gap-1">
                <Brain size={12} />
                AI Reasoning Process ({message.reasoning.length.toLocaleString()} chars)
              </summary>
              <div className="mt-2 p-2 bg-gray-800 rounded text-xs text-gray-300 whitespace-pre-wrap max-h-96 overflow-y-auto">
                {message.reasoning}
              </div>
            </details>
          )}
          
          {/* Display content without code blocks to avoid duplication */}
          <div className="text-sm whitespace-pre-wrap">{contentWithoutCodeBlocks}</div>
          
          {/* Suggested files to create */}
          {suggestedFiles.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {suggestedFiles.map((fileName, i) => (
                <button
                  key={i}
                  onClick={() => createFile(fileName, codeBlocks.find(b => b.fileName === fileName)?.code)}
                  className="px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-xs flex items-center gap-1"
                >
                  <span>+ Create {fileName}</span>
                </button>
              ))}
            </div>
          )}
          
          {/* Code blocks */}
          {codeBlocks.map((block, blockIndex) => (
            <div key={blockIndex} className="relative group">
              {block.fileName && (
                <div className="text-xs text-gray-400 mb-1">📄 {block.fileName}</div>
              )}
              <pre className="bg-black p-3 rounded text-xs overflow-x-auto">
                <code>{block.code}</code>
              </pre>
              <div className="absolute bottom-2 left-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={() => copyCode(block.code, index + blockIndex)}
                  className="p-1 bg-gray-800 hover:bg-gray-700 rounded"
                  title="Copy code"
                >
                  {copiedIndex === (index + blockIndex) ? <Check size={14} /> : <Copy size={14} />}
                </button>
                <button
                  onClick={() => applyCode(block.code, block.fileName)}
                  className="px-2 py-1 bg-p5-pink hover:bg-pink-600 rounded text-xs"
                >
                  {block.fileName ? `Apply to ${block.fileName}` : 'Apply'}
                </button>
              </div>
            </div>
          ))}
          
          {/* Apply All Code button - appears when there are code blocks */}
          {!isUser && codeBlocks.length > 0 && (
            <div className="mt-3 flex justify-start">
              <button
                onClick={() => applyAllCode(codeBlocks)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium transition-colors shadow-lg"
                title={`Apply all ${codeBlocks.length} code block${codeBlocks.length > 1 ? 's' : ''} to your project`}
              >
                Apply All ({codeBlocks.length} {codeBlocks.length === 1 ? 'file' : 'files'})
              </button>
            </div>
          )}
          
          {/* Research: Thumbs up/down feedback (only for assistant messages) */}
          {!isUser && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-gray-400">Was this helpful?</span>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => handleRating(index, 'up')}
                    className={`p-1.5 rounded transition ${
                      messageRatings[index]?.rating === 'up' 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-800 hover:bg-gray-700 text-gray-400'
                    }`}
                    title="Helpful"
                  >
                    <ThumbsUp size={14} />
                  </button>
                  <button
                    onClick={() => handleRating(index, 'down')}
                    className={`p-1.5 rounded transition ${
                      messageRatings[index]?.rating === 'down' 
                        ? 'bg-red-600 text-white' 
                        : 'bg-gray-800 hover:bg-gray-700 text-gray-400'
                    }`}
                    title="Not helpful"
                  >
                    <ThumbsDown size={14} />
                  </button>
                  {/* Submit button - only show after rating */}
                  {messageRatings[index]?.showComment && (
                    <button
                      onClick={() => submitRating(index)}
                      className="px-2 py-1 bg-p5-pink hover:bg-pink-600 rounded text-xs font-medium"
                    >
                      Submit
                    </button>
                  )}
                </div>
              </div>
              
              {/* Comment input (shows after rating) */}
              {messageRatings[index]?.showComment && (
                <div className="mt-2">
                  <input
                    type="text"
                    placeholder="Why? (optional)"
                    value={messageRatings[index]?.comment || ''}
                    onChange={(e) => handleCommentChange(index, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        submitRating(index)
                      }
                    }}
                    className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-p5-pink"
                    autoFocus
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
  
  const handleRating = (messageIndex, rating) => {
    setMessageRatings(prev => ({
      ...prev,
      [messageIndex]: {
        rating,
        comment: prev[messageIndex]?.comment || '',
        showComment: true
      }
    }))
  }
  
  const handleCommentChange = (messageIndex, comment) => {
    setMessageRatings(prev => ({
      ...prev,
      [messageIndex]: {
        ...prev[messageIndex],
        comment
      }
    }))
  }
  
  const submitRating = async (messageIndex) => {
    const ratingData = messageRatings[messageIndex]
    const message = aiMessages[messageIndex]
    // Use the message's stored iterationId for accurate attribution
    const targetIterationId = message?.iterationId || currentIterationId
    if (ratingData && targetIterationId) {
      const success = await updateIteration({
        iterationId: targetIterationId,
        rating: ratingData.rating,
        ratingComment: ratingData.comment || null
      })
      if (success) {
        showToast('Feedback submitted!', 'success')
        setMessageRatings(prev => ({
          ...prev,
          [messageIndex]: { ...prev[messageIndex], showComment: false }
        }))
      } else {
        showToast('Failed to submit feedback', 'error')
      }
    }
  }

  return (
    <div className="bg-ide-panel flex flex-col overflow-hidden h-full">
      {/* Header - Compact for mobile */}
      <div className="bg-ide-surface border-b border-ide-border flex-shrink-0">
        {/* Title Row */}
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-ide-accent" />
            <h2 className="text-sm font-medium text-ide-text">AI Assistant</h2>
          </div>
          
          <div className="flex items-center gap-0.5">
            {/* Undo AI Change button - only show if there's history */}
            {aiChangeHistory.length > 0 && (
              <button
                onClick={() => {
                  const fileName = undoLastAIChange()
                  if (fileName) {
                    showToast(`↩️ Reverted AI changes to ${fileName}`, 'success')
                  }
                }}
                className="p-1.5 text-amber-400 hover:text-amber-300 hover:bg-ide-surfaceHover rounded transition-colors"
                title={`Undo AI Change (${aiChangeHistory.length})`}
                aria-label="Undo last AI change"
              >
                <Undo2 size={14} />
              </button>
            )}
            <button
              onClick={() => setShowLibraryRef(true)}
              className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
              title="View Available Libraries"
              aria-label="View Available Libraries"
            >
              <Book size={14} />
            </button>
            <button
              onClick={() => setShowHistory(true)}
              className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
              title="Chat History"
              aria-label="Chat History"
            >
              <Clock size={14} />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
              title="AI Settings"
              aria-label="AI Settings"
            >
              <Settings size={14} />
            </button>
          </div>
        </div>

        {/* Controls Row - Provider and Mode */}
        <div className="flex items-center gap-2 px-3 pb-2">
          {/* Provider Selection */}
          <select
            value={aiProvider}
            onChange={(e) => setAiProvider(e.target.value)}
            className="flex-1 bg-ide-bg border border-ide-border rounded px-2 py-1 text-xs text-ide-text"
            data-tour="model-selector"
          >
            <option value="openai">OpenAI</option>
            <option value="anthropic">Claude</option>
            <option value="kimi">Kimi</option>
            <option value="gemini">Gemini</option>
            <option value="xelsed">Xelsed</option>
          </select>
          
          {/* Mode Selection for OpenAI */}
          {aiProvider === 'openai' && (
            <select
              value={openAIMode}
              onChange={(e) => setOpenAIMode(e.target.value)}
              className="flex-1 bg-ide-bg border border-ide-border rounded px-2 py-1 text-xs text-ide-text"
              title="Reasoning Mode"
            >
              <option value="none">⚡ None</option>
              <option value="low">🧠 Low</option>
              <option value="medium">🧠🧠 Med</option>
              <option value="high">🧠🧠🧠 High</option>
              <option value="codex">💻 Code</option>
            </select>
          )}

          {/* Model Selection for Xelsed */}
          {aiProvider === 'xelsed' && (
            <select
              value={xelsedModel}
              onChange={(e) => setXelsedModel(e.target.value)}
              className="flex-1 bg-ide-bg border border-ide-border rounded px-2 py-1 text-xs text-ide-text"
              title="Xelsed Model"
            >
              <option value="p5flash-alpha02">🎨 p5flash-alpha02</option>
              <option value="xelsed-alpha03-flash">🎨 alpha03-flash</option>
              <option value="xelsed-alpha06-flash">🎨 alpha06-flash</option>
              <option value="alpha01-test">🎨 alpha01-test</option>
            </select>
          )}
        </div>
      </div>

      
      {/* Modals */}
      {showLibraryRef && <LibraryReference isOpen={showLibraryRef} onClose={() => setShowLibraryRef(false)} />}
      {showSettings && <AiSettings onClose={() => setShowSettings(false)} />}
      {showHistory && <AiHistory isOpen={showHistory} onClose={() => setShowHistory(false)} />}

      {/* API Key Banner */}
      {showApiKeyBanner && (
        <div className="mx-4 mt-2 p-4 bg-yellow-500 bg-opacity-10 border border-yellow-500 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 text-2xl">🔑</div>
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-300 mb-1">API Key Required</h3>
              <p className="text-sm text-yellow-200 mb-3">
                The free AI quota is exhausted. Add your own OpenAI or Anthropic API key to continue.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowApiKeyBanner(false)
                    setShowSettings(true)
                  }}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm font-medium"
                >
                  Add API Key
                </button>
                <button
                  onClick={() => setShowApiKeyBanner(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recovery Banner - shows when a previous session's AI response is available */}
      {recoveredJob && (
        <div className="mx-3 mt-2 p-3 bg-green-500 bg-opacity-10 border border-green-500 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <RefreshCw size={18} className="text-green-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-green-300 text-sm mb-1">
                {recoveredJob.status === 'complete' ? 'Previous Response Recovered' : 'In-Progress Response Found'}
              </h3>
              <p className="text-xs text-green-200 mb-2 line-clamp-2">
                "{recoveredJob.prompt?.substring(0, 100)}{recoveredJob.prompt?.length > 100 ? '...' : ''}"
              </p>
              <div className="flex gap-2">
                <button
                  onClick={applyRecoveredJob}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium"
                >
                  Load Response
                </button>
                <button
                  onClick={dismissRecoveredJob}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 space-y-3">
        {aiMessages.length === 0 && (
          <div className="text-center text-ide-textMuted mt-6">
            <Sparkles size={40} className="mx-auto mb-3 text-ide-accent" />
            <p className="text-base font-medium mb-1 text-ide-text">AI Coding Assistant</p>
            <p className="text-sm">Describe what you want to create and AI will write the p5.js code for you.</p>
            <p className="text-xs mt-2 text-ide-textDim">Click <strong>Apply</strong> to add generated code to your sketch, then <strong>Run</strong> to see it!</p>
            <div className="mt-4 p-3 bg-ide-accent/10 rounded-lg border border-ide-accent/30">
              <p className="text-xs text-ide-accent font-medium">⚡ Try Turbo Mode below for faster responses!</p>
            </div>
            <div className="mt-4 text-left space-y-2 text-xs">
              <p className="font-medium text-ide-textMuted">Example prompts:</p>
              <ul className="space-y-1 text-ide-textDim">
                <li>• "Make a bouncing ball animation"</li>
                <li>• "Create a particle system that follows the mouse"</li>
                <li>• "Add rainbow colors to this sketch"</li>
                <li>• "Explain what this code does"</li>
              </ul>
            </div>
          </div>
        )}

        {aiMessages.map(renderMessage)}
        
        {/* Real-time thinking display while AI is processing */}
        {isAiThinking && (
          <div className="flex gap-3 p-3 bg-ide-elevated rounded-lg border border-ide-border">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-ide-accent/20 flex items-center justify-center">
              <Sparkles size={14} className="text-ide-accent animate-pulse" />
            </div>
            <div className="flex-1">
              {currentThinking ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-ide-text">
                    <Brain size={14} className="animate-pulse text-ide-accent" />
                    <span className="font-medium">Thinking...</span>
                  </div>
                  {/* Scrollable thinking box */}
                  <div 
                    className="text-xs text-ide-textMuted font-mono pl-4 pr-2 py-2 bg-ide-bg rounded border border-ide-border whitespace-pre-wrap"
                    style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      scrollBehavior: 'smooth'
                    }}
                    ref={(el) => {
                      if (el) el.scrollTop = el.scrollHeight
                    }}
                  >
                    {currentThinking}
                  </div>
                </div>
              ) : streamingContent ? (
                /* Real-time streaming content display */
                <div 
                  className="text-sm text-ide-text whitespace-pre-wrap"
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    scrollBehavior: 'smooth'
                  }}
                  ref={(el) => {
                    if (el) el.scrollTop = el.scrollHeight
                  }}
                >
                  {streamingContent}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex gap-1 text-ide-accent">
                    <span className="animate-bounce">●</span>
                    <span className="animate-bounce delay-100">●</span>
                    <span className="animate-bounce delay-200">●</span>
                  </div>
                  <span className="text-xs text-ide-textMuted">
                    {openAIMode === 'high' ? 'High reasoning...' :
                     openAIMode === 'medium' ? 'Medium reasoning...' :
                     openAIMode === 'low' ? 'Low reasoning...' :
                     openAIMode === 'none' ? 'Generating...' : 
                     'Processing...'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 bg-ide-surface border-t border-ide-border flex-shrink-0">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => setPromptStartTime(Date.now())}
            placeholder="Ask AI to help with your code..."
            className="flex-1 bg-ide-bg border border-ide-border rounded-lg px-3 py-2 text-sm resize-none text-ide-text placeholder-ide-textDim focus:outline-none focus:border-ide-borderFocus focus:ring-1 focus:ring-ide-accent/30"
            rows={2}
            disabled={isAiThinking}
            data-tour="ai-input"
          />
          {/* Turbo Mode Toggle */}
          <button
            onClick={() => setTurboMode(!turboMode)}
            className={`self-end p-2 rounded-lg transition-all ${
              turboMode 
                ? 'bg-yellow-500 hover:bg-yellow-600 text-black shadow-lg shadow-yellow-500/30' 
                : 'bg-ide-surfaceHover hover:bg-ide-border text-ide-textMuted'
            } ${turboMode && !isAiThinking ? 'animate-pulse' : ''}`}
            title={turboMode 
              ? `Turbo ON: Auto-apply, run & fix (${turboRetryCount}/${turboMaxRetries} retries)` 
              : 'Turbo OFF: Click to enable auto-apply, run & fix'}
            data-tour="turbo-mode"
          >
            <Zap size={18} />
          </button>
          
          {isAiThinking ? (
            <button
              onClick={handleStop}
              className="self-end p-2 bg-ide-error hover:bg-red-600 rounded-lg transition-colors"
              title="Stop AI"
            >
              <StopCircle size={18} />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="self-end p-2 bg-ide-accent hover:bg-ide-accentHover rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Send Message"
            >
              <Send size={18} />
            </button>
          )}
        </div>
        <p className="text-xs text-ide-textDim mt-1.5">
          Enter to send • Shift+Enter for new line
        </p>
      </div>
    </div>
  )
}
